import { isNotEqual } from 'vet-bones/utils';

import { DiagramSnapshot } from 'src/containers/ProjectDiagram/types/diagram';
import { ModelData } from 'src/containers/ProjectDiagram/types/model';
import {
  getModifiedClassAttributes,
  getPreviousClass,
} from 'src/containers/ProjectDiagram/utils/draft/draftClass';
import { getPreviousHierarchyConnection } from 'src/containers/ProjectDiagram/utils/draft/draftHierarchy';
import {
  getCurrentMapping,
  getPreviousMapping,
} from 'src/containers/ProjectDiagram/utils/draft/draftMapping';
import {
  getIsColorIdModified,
  getIsDescriptionModified,
  getIsLabelModified,
} from 'src/containers/ProjectDiagram/utils/draft/draftModelConcept';
import {
  getPreviousRelationship,
  getPreviousRelationshipConnection,
} from 'src/containers/ProjectDiagram/utils/draft/draftRelationship';
import { getPreviousResource } from 'src/containers/ProjectDiagram/utils/draft/draftResource';
import { transformModelToSnapshot } from 'src/containers/ProjectDiagram/utils/transformModelToSnapshot';

export const isMappingModified = (
  modelData: ModelData,
  obj: go.Shape | go.TextBlock
): boolean => {
  const { draftData } = modelData;
  if (!draftData || !obj.diagram) {
    return false;
  }

  const diagram: DiagramSnapshot = {
    id: '',
    model: transformModelToSnapshot(obj.diagram.model),
  };
  const id = obj.part?.data.value;
  const prevMapping = getPreviousMapping(diagram, id);
  const isNewMapping = !prevMapping;
  if (isNewMapping) {
    return true;
  }

  const currentMapping = getCurrentMapping(diagram, id);
  if (!currentMapping) {
    return false;
  }

  return (
    // changed mapping target
    isNotEqual(prevMapping.to, currentMapping.to) ||
    // updated mapping fields
    isNotEqual(prevMapping.fields, currentMapping.fields) ||
    // updated mapping label field
    isNotEqual(prevMapping.label, currentMapping.label) ||
    // updated mapping primary identifier field
    isNotEqual(
      prevMapping.primaryIdentifier,
      currentMapping.primaryIdentifier
    ) ||
    // updated mapping name
    isNotEqual(prevMapping.text, currentMapping.text)
  );
};

export const isClassAttributesModified = (
  modelData: ModelData,
  obj: go.Shape | go.TextBlock
): boolean => {
  const { draftData } = modelData;
  if (!draftData || !obj.diagram) {
    return false;
  }

  const diagram: DiagramSnapshot = {
    id: '',
    model: transformModelToSnapshot(obj.diagram.model),
  };
  const classIri = obj.part?.data.value;
  const isNewClass = !getPreviousClass(diagram, classIri);
  if (isNewClass) {
    return true;
  }

  const classAttributes = Object.fromEntries(
    Object.entries(modelData.attributesByIri).filter(
      ([, attribute]) =>
        Object.keys(attribute.domains).includes(classIri) ||
        Object.keys(attribute.ranges).includes(classIri)
    )
  );

  const modifiedAttributes = getModifiedClassAttributes({
    classAttributes,
    classIri,
    isNewClass,
    diagram,
  });

  return Boolean(modifiedAttributes.size);
};

export const isClassModified = (
  modelData: ModelData,
  obj: go.Shape | go.TextBlock
): boolean => {
  const { draftData } = modelData;
  if (!draftData || !obj.diagram) {
    return false;
  }

  const diagram: DiagramSnapshot = {
    id: '',
    model: transformModelToSnapshot(obj.diagram.model),
  };
  const iri = obj.part?.data.value;
  const isNewClass = !getPreviousClass(diagram, iri);
  if (isNewClass) {
    return true;
  }

  const isColorIdModified = getIsColorIdModified({
    iri,
    isNewConcept: isNewClass,
    diagram,
  });

  const isDescriptionModified = getIsDescriptionModified({
    iri,
    isNewConcept: isNewClass,
    diagram,
  });

  const isLabelModified = getIsLabelModified({
    iri,
    isNewConcept: isNewClass,
    diagram,
  });

  return isColorIdModified || isDescriptionModified || isLabelModified;
};

export const isHierarchyModified = (
  modelData: ModelData,
  obj: go.Shape | go.TextBlock
): boolean => {
  const { draftData } = modelData;
  if (!draftData || !obj.diagram) {
    return false;
  }

  const diagram: DiagramSnapshot = {
    id: '',
    model: transformModelToSnapshot(obj.diagram.model),
  };
  const data = obj.part?.data;
  const isNewHierarchyConnection = !getPreviousHierarchyConnection(
    diagram,
    data
  );

  return isNewHierarchyConnection;
};

export const isRelationshipModified = (
  modelData: ModelData,
  obj: go.Shape | go.TextBlock
): boolean => {
  const { draftData } = modelData;
  if (!draftData || !obj.diagram) {
    return false;
  }

  const diagram: DiagramSnapshot = {
    id: '',
    model: transformModelToSnapshot(obj.diagram.model),
  };
  const data = obj.part?.data;
  const isNewRelationshipConnection = !getPreviousRelationshipConnection(
    diagram,
    data
  );
  if (isNewRelationshipConnection) {
    return true;
  }

  const iri = obj.part?.data.value;
  const isNewRelationship = !getPreviousRelationship(diagram, iri);
  if (isNewRelationship) {
    return true;
  }

  const isDescriptionModified = getIsDescriptionModified({
    iri,
    isNewConcept: isNewRelationship,
    diagram,
  });

  const isLabelModified = getIsLabelModified({
    iri,
    isNewConcept: isNewRelationship,
    diagram,
  });

  return isDescriptionModified || isLabelModified;
};

export const isResourceModified = (
  modelData: ModelData,
  obj: go.Shape | go.TextBlock
) => {
  const { draftData } = modelData;
  if (!draftData || !obj.diagram) {
    return false;
  }

  const diagram: DiagramSnapshot = {
    id: '',
    model: transformModelToSnapshot(obj.diagram.model),
  };
  const id = obj.part?.data.value;
  const isNewResource = !getPreviousResource(diagram, id);

  return isNewResource;
};
